import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { UserGroupIcon, EnvelopeIcon, PhoneIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/common/Card';
import { Badge } from '../components/common/Badge';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Alert } from '../components/common/Alert';
import { Link } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import { useNotification } from '../contexts/NotificationContext';

interface TeamMember {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  position?: string;
  created_at: string;
  hire_date?: string;
  phone?: string;
  profile_image?: string;
  is_active: boolean;
  roles: Array<{
    id: number;
    name: string;
    description: string;
  }>;
  department?: {
    id: number;
    name: string;
  };
  departments?: Array<{
    id: number;
    name: string;
  }>;
  teams?: Array<{
    id: number;
    name: string;
    department_id: number;
  }>;
  manager?: {
    id: number;
    first_name: string;
    last_name: string;
  };
  currentLeave?: {
    id: string;
    startDate: string;
    endDate: string;
    leaveType: {
      name: string;
    };
  };
}

export const Team = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<string>('name'); // Default sort by name
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const { user, isManager, isAdmin, isHR } = usePermissions();
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  
  const { data: teamData, isLoading, error } = useQuery({
    queryKey: ['teamMembers', user?.id, currentPage, sortField, sortDirection],
    queryFn: async () => {
      try {
        console.log('Fetching team members with params:', { page: currentPage, limit: 20, sort: sortField, order: sortDirection });
        // Use users endpoint to get all team members with full data
        const response = await api.get('/users', {
          params: { 
            page: currentPage, 
            limit: 20, // Zwiększamy limit do 20 na stronę
            sort: sortField,
            order: sortDirection,
            include: 'departments,teams' // Request departments and teams data to be included
          }
        });
        
        console.log('Team members response:', response.data);
        
        const result = {
          users: response.data.users || response.data || [],
          total: response.data.total || (response.data.users ? response.data.users.length : 0),
          totalPages: response.data.totalPages || 1,
          currentPage: response.data.page || currentPage
        };
        
        if (isManager && !isAdmin && !isHR) {
          // Filter to show only team members that report to this manager
          result.users = result.users.filter((member: TeamMember) => 
            member.manager?.id === user?.id || 
            member.teams?.some(team => user?.teams?.some(userTeam => userTeam.id === team.id))
          );
        }
        
        console.log('Processed team data:', result);
        return result;
      } catch (error) {
        console.error('Team loading error:', error);
        throw error;
      }
    },
    retry: 2,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000, // 5 minutes
    onError: (error: any) => {
      console.error('Team query error:', error);
      showNotification({
        type: 'error',
        title: t('errors.loadingTeam', 'Błąd ładowania zespołu'),
        message: t('errors.loadingTeamMessage', 'Nie udało się załadować danych zespołu. Spróbuj odświeżyć stronę.')
      });
    }
  });

  const { data: departments, isError: departmentsError } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      try {
        console.log('Fetching departments...');
        const response = await api.get('/departments');
        console.log('Departments response:', response.data);
        return response.data.departments || response.data || [];
      } catch (error) {
        console.error('Departments loading error:', error);
        return [];
      }
    },
    retry: 2,
    retryDelay: 1000,
    staleTime: 10 * 60 * 1000, // 10 minutes
    onError: (error: any) => {
      console.error('Departments query error:', error);
    }
  });

  // Funkcja pomocnicza do znajdowania departamentów użytkownika przez przynależność do zespołów
  const getDepartmentFromTeams = (member: TeamMember, allDepartments: any[]) => {
    if (!member.teams || member.teams.length === 0) {
      return null;
    }
    
    const teamDepartmentIds = member.teams.map(team => team.department_id);
    const departmentsFromTeams = allDepartments.filter(dept => 
      teamDepartmentIds.includes(dept.id)
    );
    
    return departmentsFromTeams.length > 0 ? departmentsFromTeams[0] : null;
  };

  const teamMembers = teamData?.users || [];
  const totalUsers = teamData?.total || 0;
  const totalPages = teamData?.totalPages || 1;
  
  const filteredMembers = teamMembers.filter((member: TeamMember) => {
    const matchesSearch = searchTerm === '' || 
      `${member.first_name} ${member.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.position?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Check department, departments array, and teams department_id
    const departmentFromTeam = getDepartmentFromTeams(member, departments || []);
    const matchesDepartment = selectedDepartment === 'all' || 
      member.department?.id.toString() === selectedDepartment ||
      member.departments?.some(dept => dept.id.toString() === selectedDepartment) ||
      (departmentFromTeam && departmentFromTeam.id.toString() === selectedDepartment);
    
    return matchesSearch && matchesDepartment;
  });

  if (isLoading) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  );

  if (error) return (
    <div className="space-y-4">
      <Alert variant="error">
        <div>
          <h3 className="font-medium">{t('errors.loadingTeam', 'Błąd ładowania zespołu')}</h3>
          <p className="mt-1 text-sm">{t('errors.loadingTeamMessage', 'Nie udało się załadować danych zespołu. Spróbuj odświeżyć stronę.')}</p>
        </div>
      </Alert>
      <div className="flex gap-2">
        <Button onClick={() => window.location.reload()} variant="primary">
          {t('common.refresh', 'Odśwież')}
        </Button>
        <Button onClick={() => setCurrentPage(1)} variant="outline">
          {t('common.retry', 'Spróbuj ponownie')}
        </Button>
      </div>
    </div>
  );

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('teamPage.title')}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('teamPage.subtitle', 'View and manage your team members')}
        </p>
        {departmentsError && (
          <Alert variant="warning" className="mt-4">
            {t('errors.departmentsLoadingFailed', 'Nie udało się załadować działów. Filtrowanie może nie działać poprawnie.')}
          </Alert>
        )}
      </div>

      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          placeholder={t('teamPage.search')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        
        {/* Sort Dropdown */}
        <div className="flex gap-2">
          <select
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            value={sortField}
            onChange={(e) => setSortField(e.target.value)}
          >
            <option value="name">{t('teamPage.sortName', 'Name')}</option>
            <option value="department">{t('teamPage.sortDepartment', 'Department')}</option>
            <option value="position">{t('teamPage.sortPosition', 'Position')}</option>
            <option value="hire_date">{t('teamPage.sortHireDate', 'Hire Date')}</option>
          </select>
          
          <button
            onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
            className="p-2 border border-gray-300 rounded-md"
            title={sortDirection === 'asc' ? t('teamPage.sortAscending', 'Ascending') : t('teamPage.sortDescending', 'Descending')}
          >
            {sortDirection === 'asc' ? '↑' : '↓'}
          </button>
        </div>
        
        {/* Department Filter */}
        <select
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          value={selectedDepartment}
          onChange={(e) => setSelectedDepartment(e.target.value)}
        >
          <option value="all">{t('teamPage.all')} {t('teamPage.departments')}</option>
          {departments?.map((dept: any) => (
            <option key={dept.id} value={dept.id}>
              {dept.name}
            </option>
          ))}
        </select>
      </div>

      {/* Team Members Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredMembers.map((member: TeamMember) => (
          <Card key={member.id}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary-500">
                      <span className="text-lg font-medium leading-none text-white">
                        {member.first_name[0]}{member.last_name[0]}
                      </span>
                    </span>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      {member.first_name} {member.last_name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {member.position || member.roles?.[0]?.name || 'Employee'}
                    </p>
                  </div>
                </div>
              </div>

              <dl className="space-y-2">
                <div className="flex items-center text-sm">
                  <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">{member.email}</span>
                </div>
                {member.phone && (
                  <div className="flex items-center text-sm">
                    <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-gray-600">{member.phone}</span>
                  </div>
                )}
                <div className="flex items-center text-sm">
                  <UserGroupIcon className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">
                    {member.department?.name || 
                     member.departments?.[0]?.name || 
                     getDepartmentFromTeams(member, departments || [])?.name || 
                     t('team.noDepartment')}
                  </span>
                </div>
                
                {/* Teams */}
                {member.teams && member.teams.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {member.teams.map(team => (
                      <span key={team.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        {team.name}
                      </span>
                    ))}
                  </div>
                )}
              </dl>

              <div className="mt-4 pt-4 border-t border-gray-200">
                {member.currentLeave ? (
                  <div className="flex items-center">
                    <CalendarIcon className="h-4 w-4 text-yellow-500 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">
                        {t('teamPage.currentlyOnLeave')} {member.currentLeave.leaveType.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {t('teamPage.until')} {format(new Date(member.currentLeave.endDate), 'MMM d, yyyy')}
                      </p>
                    </div>
                  </div>
                ) : (
                  <Badge variant="success">{t('teamPage.available', 'Available')}</Badge>
                )}
              </div>

              <div className="mt-4 flex gap-2">
                <Link to={`/users/${member.id}`} className="flex-1">
                  <Button variant="outline" size="sm" className="w-full">
                    {t('teamPage.viewProfile')}
                  </Button>
                </Link>
                <Link to={`/leaves/new?substituteId=${member.id}`} className="flex-1">
                  <Button variant="outline" size="sm" className="w-full">
                    {t('teamPage.selectAsSubstitute')}
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredMembers.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('teamPage.noTeamMembers')}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {t('teamPage.noTeamMembersDesc', 'Try adjusting your search or filter criteria')}
            </p>
          </CardContent>
        </Card>
      )}
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex justify-center">
          <div className="flex rounded-md shadow-sm">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-medium text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.previous', 'Previous')}
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ring-1 ring-inset ring-gray-300 focus:z-10 ${
                  currentPage === page 
                    ? 'bg-primary-600 text-white hover:bg-primary-700' 
                    : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="relative inline-flex items-center rounded-r-md px-3 py-2 text-sm font-medium text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.next', 'Next')}
            </button>
          </div>
        </div>
      )}
      
      {/* Total count */}
      <div className="mt-4 text-center text-sm text-gray-500">
        {t('teamPage.showing', 'Showing')} {filteredMembers.length} {t('teamPage.of', 'of')} {totalUsers} {t('teamPage.members', 'members')}
      </div>
    </div>
  );
};